import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BankAssuranceService } from 'src/app/_services/bank-assurance.service';
import { ToastService } from 'src/app/_models/toast.service';
import { finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface ClaimDetailData {
  id?: number;
  claimNumber?: string;
  contratManualNumber?: string;
  companyCode?: number;
  validedBy?: number;
  status?: string;
  bankId?: number;
  idagence?: number;
  policyID?: string;
  // propriétés optionnelles ajoutées pour couvrir différentes formes de données
  policyIDHelios?: string;
  policy?: { id?: string } | any;
  policyNumber?: string;
  productName?: string;
  dateMinimum?: string;
  incidenceDate?: string;
  notificationDate?: string;
  riskElement?: string;
  riskElementDetail?: string;
  riskElementID?: number;
  currentTab?: string;
  claimDetail?: any;
  evaluationItems?: any[];
  createdAt?: string;
  createdBy?: number;
  fromHistory?: boolean;
}

@Component({
  selector: 'app-modal-transferclaim',
  templateUrl: './modal-transferclaim.component.html',
  styleUrls: ['./modal-transferclaim.component.scss']
})
export class ModalTransferClaimComponent implements OnInit {
  public showActions = false;
  documentLabelsMap: { [key: number]: string } = {};
  isLoadingDocumentLabels = false;
  uploadedDocuments: Array<{ id: number; filePath: string; label: string }> = [];
  requestedDocuments: Array<{ id: number; label: string; labelId: number }> = [];
  downloadingDocId: number | null = null;
  private userId = localStorage.getItem('ACTIVA_REDACTEUR_BANCASSURANCEUSERID');

  constructor(
    public dialogRef: MatDialogRef<ModalTransferClaimComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClaimDetailData,
    private http: HttpClient,
    private bankAssuranceService: BankAssuranceService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    // load labels and separate documents when modal opens
    try {
      this.loadDocumentLabelsFromAPI();
    } catch (e) { /* noop */ }
    this.separateDocuments();
  }

  private loadDocumentLabelsFromAPI(): void {
    const warrantyCode = this.data.claimDetail?.warrantyCode || String(this.data.claimDetail?.subRick8ElementID || '');
    if (!warrantyCode) {
      this.documentLabelsMap = {};
      this.separateDocuments();
      return;
    }

    this.isLoadingDocumentLabels = true;
    this.bankAssuranceService.authenticate(parseInt(this.userId || '0')).subscribe({
      next: (authResponse: any) => {
        if (!authResponse?.token) { this.documentLabelsMap = {}; this.isLoadingDocumentLabels = false; this.separateDocuments(); return; }

        const headers = new HttpHeaders()
          .set('Authorization', `Bearer ${authResponse.token}`)
          .set('Ocp-Apim-Subscription-Key', environment.Ocp_Apim_Subscription_Key);

        const url = `${environment.MSBank}/api/Warranty/Documents/ByWarrantyCode/${warrantyCode}`;
        this.http.get<any>(url, { headers }).pipe(finalize(() => this.isLoadingDocumentLabels = false)).subscribe({
          next: (docs: any[]) => {
            if (Array.isArray(docs) && docs.length) {
              this.documentLabelsMap = {};
              docs.forEach(d => { if (d.status === '1') this.documentLabelsMap[d.id] = d.name; });
            } else {
              this.documentLabelsMap = {};
            }
            this.separateDocuments();
          },
          error: () => { this.documentLabelsMap = {}; this.separateDocuments(); }
        });
      },
      error: () => { this.documentLabelsMap = {}; this.isLoadingDocumentLabels = false; this.separateDocuments(); }
    });
  }

  private separateDocuments(): void {
    const comps = (this.data?.claimDetail?.complementClaim) || [];
    this.uploadedDocuments = comps
      .filter((d: any) => d.filePath && d.filePath.trim() !== '')
      .map((d: any) => {
        const labelId = d.label;
        const label = this.documentLabelsMap[labelId] || d.documentName || d.name || (labelId != null ? `Document ${labelId}` : `Document ${d.id || 'unknown'}`);
        return { id: d.id, filePath: d.filePath, label };
      });

    this.requestedDocuments = comps
      .filter((d: any) => !d.filePath || d.filePath.trim() === '')
      .map((d: any) => {
        const labelId = d.label;
        const label = this.documentLabelsMap[labelId] || d.documentName || d.name || (labelId != null ? `Document ${labelId}` : `Document ${d.id || 'unknown'}`);
        return { id: d.id, label, labelId: d.label };
      });
  }

  downloadDocument(doc: any): void {
    if (!doc || !doc.id) return;
    this.downloadingDocId = doc.id;
    this.bankAssuranceService.authenticate(parseInt(this.userId || '0')).subscribe({
      next: (authResponse: any) => {
        if (!authResponse?.token) { this.toastService.showErrorToast('Échec de l\'authentification'); this.downloadingDocId = null; return; }

        const headers = new HttpHeaders()
          .set('Authorization', `Bearer ${authResponse.token}`)
          .set('Ocp-Apim-Subscription-Key', environment.Ocp_Apim_Subscription_Key);

        this.http.get<any>(`${environment.MSClaim}/api/documents/${doc.id}/download-url`, { headers }).pipe(finalize(() => this.downloadingDocId = null)).subscribe({
          next: (response: any) => {
            if (response?.downloadUrl) window.open(response.downloadUrl, '_blank');
            else this.toastService.showErrorToast('Impossible de télécharger le document');
          },
          error: () => { this.toastService.showErrorToast('Erreur lors du téléchargement'); }
        });
      },
      error: () => { this.toastService.showErrorToast('Échec de l\'authentification'); this.downloadingDocId = null; }
    });
  }

  previewDocument(doc: any): void { this.downloadDocument(doc); }

  isPreviewable(filePath?: string): boolean {
    if (!filePath) return false;
    const ext = this.getFileExtension(filePath).toLowerCase();
    return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
  }

  getFileExtension(filePath?: string): string {
    if (!filePath) return '';
    const parts = filePath.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  public toggleActions(): void {
    if (this.data?.fromHistory) return;
    this.showActions = !this.showActions;
  }

  public onTransferAction(): void { this.transfer(); }

  public transfer(): void {
    this.dialogRef.close({ action: 'transfer', claim: this.data });
  }

  public close(): void { this.dialogRef.close(); }

  public isInvalidated(): boolean {
    const s = (this.data?.status || '').toLowerCase();
    return ['invalidated', 'invalidé'].includes(s);
  }

  public getStatusClass(status?: string): string {
    switch ((status || '').toLowerCase()) {
      case 'proposal': return 'status-proposal';
      case 'initial': return 'status-initial';
      case 'draft': case 'brouillon': return 'status-draft';
      case 'courant': case 'validated': case 'validé': return 'status-validated';
      case 'clos': case 'closed': case 'fermé': return 'status-closed';
      case 'annulé': case 'cancelled': case 'invalidated': case 'invalidé': return 'status-cancelled';
      default: return 'status-default';
    }
  }

  public getStatusLabel(status?: string): string {
    switch ((status || '').toLowerCase()) {
      case 'proposal': return 'Proposal';
      case 'initial': return 'Initial';
      case 'draft': case 'brouillon': return 'Brouillon';
      case 'validated': case 'validé': case 'courant': return 'Validé';
      case 'closed': case 'clos': case 'fermé': return 'Clôturé';
      case 'cancelled': case 'annulé': return 'Annulé';
      case 'invalidated': case 'invalidé': return 'Invalidé';
      default: return status || 'N/A';
    }
  }

  public getFileName(filePath?: string): string {
    if (!filePath) return 'Fichier inconnu';
    const parts = filePath.split('/');
    return parts[parts.length - 1];
  }

  public getFileIcon(filePath?: string): string {
    if (!filePath) return 'file';
    const ext = filePath.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'file-pdf';
      case 'doc': case 'docx': return 'file-text';
      case 'xls': case 'xlsx': return 'table';
      case 'jpg': case 'jpeg': case 'png': case 'gif': return 'image';
      default: return 'file';
    }
  }

  public getValidDocuments(): Array<{ id: number; filePath: string; label: string }> {
    return this.uploadedDocuments || [];
  }

  public getValidDocumentsCount(): number { return this.uploadedDocuments.length; }

  public getDocumentLabel(labelId: number): string { return this.documentLabelsMap[labelId] || `Document ${labelId}`; }
}
